import { type FC, useCallback } from "react";
import Markdown from "react-markdown";
import { useSearchParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

import { updateSearchParams } from "../../utils/searchParams";
import { Button } from "../common/Button";
import { DocumentIcon } from "../common/icons";
import { Modal } from "../common/Modal";
import { Text } from "../common/Text";

const QA_MANDATE_PARAM = "overview_qa_mandate";

function getPreviewLine(markdown: string): string {
  for (const raw of markdown.split(/\r?\n/)) {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith("#")) {
      continue;
    }
    return trimmed
      .replace(/^[-*]\s+/, "")
      .replace(/^\d+\.\s+/, "")
      .trim();
  }
  return "Step-by-step QA guide";
}

export const ExecutionQaMandateCard: FC<{ qaMandate: string }> = ({
  qaMandate,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const open = searchParams.get(QA_MANDATE_PARAM) === "1";

  const handleOpen = useCallback(() => {
    setSearchParams(
      (prev) => updateSearchParams(prev, { [QA_MANDATE_PARAM]: "1" }),
      { replace: true },
    );
  }, [setSearchParams]);

  const handleClose = useCallback(() => {
    setSearchParams(
      (prev) => updateSearchParams(prev, { [QA_MANDATE_PARAM]: null }),
      { replace: true },
    );
  }, [setSearchParams]);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleOpen}
        className="w-full items-center gap-2.5 rounded-none border border-slate-300 bg-white px-3 py-2.5 text-left shadow-[4px_4px_0_rgba(15,23,42,0.08)] hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950/50 dark:text-white dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)] dark:hover:bg-slate-900/80"
        title="View QA mandate"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-900/80">
          <DocumentIcon
            size={16}
            className="text-slate-500 dark:text-slate-300"
          />
        </div>
        <div className="min-w-0 flex-1">
          <span className="block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-800 dark:text-slate-100">
            QA Mandate
          </span>
          <Text as="p" className="truncate">
            {getPreviewLine(qaMandate)}
          </Text>
        </div>
        <svg
          className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </Button>

      {open && (
        <Modal
          title="QA Mandate"
          icon={<DocumentIcon size={16} />}
          onClose={handleClose}
        >
          <article className="markdown-content">
            <Markdown remarkPlugins={[remarkGfm]}>{qaMandate}</Markdown>
          </article>
        </Modal>
      )}
    </>
  );
};
